/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { inputClass, selectStyles } from '../helpers';
import {
  FREQUENCY_OPTIONS,
  RadioOption,
  SALES_TEAM_OPTIONS,
  TEAM_ACCESS_OPTIONS,
} from '../options';
import { DeliveryFrequency, SalesOperationValues } from '../types';

const MIN_USAGE_LENGTH = 10;
const MAX_USAGE_LENGTH = 1000;

type FieldName = keyof SalesOperationValues;
type SalesOperationErrors = Partial<Record<FieldName, string>>;

interface SalesOperationStepProps {
  values: SalesOperationValues;
  onChange: (values: SalesOperationValues) => void;
  onNext: () => void;
  onBack: () => void;
  onReturnToReview: (() => void) | undefined;
}

interface RadioGroupProps<T extends string> {
  name: string;
  legend: string;
  options: RadioOption<T>[];
  value: T | '';
  error: string | undefined;
  onChange: (value: T) => void;
}

const RadioGroup = <T extends string>({
  name,
  legend,
  options,
  value,
  error,
  onChange,
}: RadioGroupProps<T>) => (
  <fieldset className="mb-4">
    <legend className="col-form-label pt-0">{legend}</legend>
    {options.map((option) => {
      const id = `${name}_${option.value}`;

      return (
        <div key={option.value} className="form-check">
          <input
            id={id}
            type="radio"
            name={name}
            className={`form-check-input ${error ? 'is-invalid' : ''}`}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <label className="form-check-label" htmlFor={id}>
            {option.label} <span className="text-muted">({option.description})</span>
          </label>
        </div>
      );
    })}
    {error && <div className="invalid-feedback d-block">{error}</div>}
  </fieldset>
);

const validate = (values: SalesOperationValues): SalesOperationErrors => {
  const errors: SalesOperationErrors = {};

  if (!values.equipe_vendas) {
    errors.equipe_vendas = 'Selecione uma opção.';
  }

  if (!values.acesso_equipe) {
    errors.acesso_equipe = 'Selecione uma opção.';
  }

  if (!/^\d+$/.test(values.qtd_pessoas) || Number(values.qtd_pessoas) < 1) {
    errors.qtd_pessoas = 'Informe a quantidade de pessoas, com pelo menos 1.';
  }

  if (values.uso_licitacoes.trim().length < MIN_USAGE_LENGTH) {
    errors.uso_licitacoes = 'Conte em poucas palavras como as licitações serão usadas.';
  }

  if (values.sistema_destino.trim().length < 2) {
    errors.sistema_destino = 'Informe o nome do sistema.';
  }

  if (values.motor_busca_nome.trim().length < 2) {
    errors.motor_busca_nome = 'Informe o nome da aplicação.';
  }

  // Um reenvio de ajuste pode trazer "mais_3x_dia", opção que saiu do formulário.
  if (!FREQUENCY_OPTIONS.some((option) => option.value === values.frequencia)) {
    errors.frequencia = 'Selecione a frequência.';
  }

  return errors;
};

export const isSalesOperationComplete = (values: SalesOperationValues) =>
  Object.keys(validate(values)).length === 0;

const SalesOperationStep: React.FC<SalesOperationStepProps> = ({
  values,
  onChange,
  onNext,
  onBack,
  onReturnToReview,
}) => {
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({});
  const [submitted, setSubmitted] = useState<boolean>(false);

  const errors = useMemo(() => validate(values), [values]);
  const isValid = Object.keys(errors).length === 0;

  const touch = (field: FieldName) => setTouched((prev) => ({ ...prev, [field]: true }));

  const fieldError = (field: FieldName) =>
    submitted || touched[field] ? errors[field] : undefined;

  const handleChange = <K extends FieldName>(field: K, value: SalesOperationValues[K]) =>
    onChange({ ...values, [field]: value });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    if (isValid) onNext();
  };

  const handleReturnToReview = () => {
    setSubmitted(true);

    if (isValid && onReturnToReview) onReturnToReview();
  };

  const peopleError = fieldError('qtd_pessoas');
  const usageError = fieldError('uso_licitacoes');
  const systemError = fieldError('sistema_destino');
  const applicationError = fieldError('motor_busca_nome');
  const frequencyError = fieldError('frequencia');

  const selectedFrequency =
    FREQUENCY_OPTIONS.find((option) => option.value === values.frequencia) || null;

  return (
    <form noValidate onSubmit={handleSubmit}>
      <p className="card-text font-weight-bold mt-4">Operação de vendas</p>
      <RadioGroup
        name="api_request_equipe_vendas"
        legend="Como funciona a sua equipe de vendas ao governo?"
        options={SALES_TEAM_OPTIONS}
        value={values.equipe_vendas}
        error={fieldError('equipe_vendas')}
        onChange={(value) => handleChange('equipe_vendas', value)}
      />
      <RadioGroup
        name="api_request_acesso_equipe"
        legend="Como a sua equipe de vendas vai acessar as licitações recebidas pela API?"
        options={TEAM_ACCESS_OPTIONS}
        value={values.acesso_equipe}
        error={fieldError('acesso_equipe')}
        onChange={(value) => handleChange('acesso_equipe', value)}
      />

      <div className="mb-4">
        <label htmlFor="api_request_qtd_pessoas">
          Quantas pessoas trabalham no setor de licitação da sua empresa?
        </label>
        <div className="row">
          <div className="col-6 col-md-3">
            <input
              id="api_request_qtd_pessoas"
              type="text"
              inputMode="numeric"
              maxLength={5}
              className={inputClass(peopleError)}
              value={values.qtd_pessoas}
              onChange={(e) => handleChange('qtd_pessoas', e.target.value.replace(/\D/g, ''))}
              onBlur={() => touch('qtd_pessoas')}
            />
            {peopleError && <div className="invalid-feedback">{peopleError}</div>}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="api_request_uso_licitacoes">
          Como as licitações recebidas pela API serão usadas na sua empresa?
        </label>
        <textarea
          id="api_request_uso_licitacoes"
          rows={4}
          maxLength={MAX_USAGE_LENGTH}
          className={inputClass(usageError)}
          value={values.uso_licitacoes}
          onChange={(e) => handleChange('uso_licitacoes', e.target.value)}
          onBlur={() => touch('uso_licitacoes')}
        />
        {usageError && <div className="invalid-feedback">{usageError}</div>}
        <small className="form-text text-muted text-right">
          {values.uso_licitacoes.length}/{MAX_USAGE_LENGTH}
        </small>
      </div>

      <p className="card-text font-weight-bold mt-4">Sistema de destino</p>
      <div className="mb-4">
        <label htmlFor="api_request_sistema_destino">
          Para qual sistema as licitações serão exportadas?
        </label>
        <input
          id="api_request_sistema_destino"
          type="text"
          className={inputClass(systemError)}
          value={values.sistema_destino}
          onChange={(e) => handleChange('sistema_destino', e.target.value)}
          onBlur={() => touch('sistema_destino')}
        />
        {systemError && <div className="invalid-feedback">{systemError}</div>}
      </div>

      <div className="mb-4">
        <label htmlFor="api_request_motor_busca_nome">
          Nome da aplicação (&ldquo;motor de busca&rdquo;) que vai receber as licitações da API
        </label>
        <input
          id="api_request_motor_busca_nome"
          type="text"
          className={inputClass(applicationError)}
          value={values.motor_busca_nome}
          onChange={(e) => handleChange('motor_busca_nome', e.target.value)}
          onBlur={() => touch('motor_busca_nome')}
        />
        {applicationError && <div className="invalid-feedback">{applicationError}</div>}
      </div>

      <div className="row">
        <div className="col-md-6 mb-4">
          <label htmlFor="api_request_frequencia">
            Com qual frequência essa aplicação vai receber as licitações?
          </label>
          <Select
            inputId="api_request_frequencia"
            options={FREQUENCY_OPTIONS}
            value={selectedFrequency}
            onChange={(option) =>
              handleChange('frequencia', (option ? option.value : '') as DeliveryFrequency)
            }
            onBlur={() => touch('frequencia')}
            placeholder="Selecionar"
            styles={selectStyles(Boolean(frequencyError))}
          />
          {frequencyError && <div className="invalid-feedback d-block">{frequencyError}</div>}
          <small className="form-text text-muted">
            Recomendamos receber as licitações 3 vezes por dia.
          </small>
        </div>
      </div>

      {submitted && !isValid && (
        <div className="alert alert-danger text-center py-2" role="alert">
          Revise os campos destacados para continuar.
        </div>
      )}

      <div className="row mt-2">
        <div className="col-md-12">
          <button type="submit" className="btn btn-lg btn-block btn-submit">
            Continuar
          </button>
          <div className="text-center mt-2">
            {onReturnToReview && (
              <button type="button" className="btn btn-link" onClick={handleReturnToReview}>
                Voltar para a revisão
              </button>
            )}
            <button type="button" className="btn btn-link" onClick={onBack}>
              Voltar
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default SalesOperationStep;
